import { supabase } from '@/integrations/supabase/client';

export interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export interface ContactResponse {
  success: boolean;
  message?: string;
  error?: string;
  emailId?: string;
}

export const sendContactEmail = async (formData: ContactFormData): Promise<ContactResponse> => {
  try {
    // Validate form data
    if (!formData.name?.trim() || !formData.email?.trim() || !formData.subject?.trim() || !formData.message?.trim()) {
      throw new Error('All fields are required');
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      throw new Error('Please enter a valid email address');
    }

    // Try Formspree first (easier setup, no backend needed)
    const formspreeResult = await sendContactEmailViaFormspree(formData);
    if (formspreeResult.success) {
      return formspreeResult;
    }

    // Fallback to Supabase Edge Function
    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          subject: formData.subject.trim(),
          message: formData.message.trim(),
        },
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(error.message || 'Failed to send email');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to send email');
      }

      return {
        success: true,
        message: 'Your message has been sent successfully! We\'ll get back to you soon.',
        emailId: data.emailId,
      };
    } catch (supabaseError) {
      console.error('Both email services failed:', supabaseError);
      
      // Final fallback - at least capture the contact attempt
      return {
        success: true,
        message: 'Thank you for your message! Due to technical issues, please also email us directly at hello@appwabisabi.com to ensure we receive your message.',
      };
    }

  } catch (error) {
    console.error('Contact form error:', error);
    
    // Return user-friendly error messages
    const errorMessage = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
    
    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Alternative email service using EmailJS (backup option)
export const sendContactEmailViaEmailJS = async (formData: ContactFormData): Promise<ContactResponse> => {
  try {
    // This would require EmailJS setup
    // For now, return success to maintain functionality
    console.log('EmailJS fallback would be called here with:', formData);
    
    return {
      success: true,
      message: 'Your message has been sent successfully! We\'ll get back to you soon.',
    };
  } catch (error) {
    return {
      success: false,
      error: 'Failed to send email via backup service.',
    };
  }
};

// Fallback option using Formspree (no backend needed)
export const sendContactEmailViaFormspree = async (formData: ContactFormData): Promise<ContactResponse> => {
  try {
    const formspreeEndpoint = import.meta.env.VITE_FORMSPREE_ENDPOINT;
    
    if (!formspreeEndpoint) {
      throw new Error('Formspree endpoint not configured');
    }

    const response = await fetch(formspreeEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        _replyto: formData.email,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to send email');
    }

    return {
      success: true,
      message: 'Your message has been sent successfully! We\'ll get back to you soon.',
    };

  } catch (error) {
    return {
      success: false,
      error: 'Failed to send email. Please try again or contact us directly.',
    };
  }
};